import { AuthenticationTriggerWrapper } from '@/components/buttons/AuthenticationTriggerWrapper';
import { Attribute } from '@/components/features/Search/Facets/useFacets';
import { env } from '@/env.mjs';
import { useMarketplaceChain } from '@/hooks/use-marketplace-chain';
import { useAuthentication } from '@/hooks/useAuthentication';
import { getBidCurrencies } from '@/utils/getChainCurrencies';
import { ReservoirAction } from '@/utils/reservoir';
import { routes } from '@/utils/routes';
import { Button, ButtonProps, IconButton } from '@chakra-ui/react';
import CollectionOfferIcon from '@sphere/icons/components/CollectionOffer';
import { BidModal, BidStep } from '@sphere/reservoir-kit-ui';
import useTranslation from 'next-translate/useTranslation';
import { useRouter } from 'next/router';
import { rem } from 'polished';
import { type ComponentPropsWithoutRef, type FC } from 'react';
import { toast } from 'sonner';
import { type SWRResponse } from 'swr';
import ActionDisabledTooltip from './ActionDisabledTooltip';

type Props = ButtonProps & {
  collectionId?: string;
  attribute?: Attribute;
  mutate?: SWRResponse['mutate'];
  label: string;
  isIconButton?: boolean;
  openState: ComponentPropsWithoutRef<typeof BidModal>['openState'];
};

const CollectionOffer: FC<Props> = ({
  collectionId,
  attribute,
  mutate,
  label,
  isIconButton,
  openState,
  ...buttonProps
}) => {
  const router = useRouter();
  const { userAddress } = useAuthentication();
  const { t } = useTranslation('common');
  const marketplaceChain = useMarketplaceChain();

  const Trigger = (
    <ActionDisabledTooltip action={ReservoirAction.Bid}>
      {isIconButton ? (
        <IconButton
          icon={<CollectionOfferIcon />}
          variant="secondary"
          rounded="full"
          aria-label={label}
          title={label}
          {...buttonProps}
        />
      ) : (
        <Button
          variant="secondary"
          leftIcon={<CollectionOfferIcon boxSize={rem(16)} />}
          {...buttonProps}
        >
          {label}
        </Button>
      )}
    </ActionDisabledTooltip>
  );

  return (
    <AuthenticationTriggerWrapper trigger={Trigger}>
      <BidModal
        collectionId={collectionId}
        trigger={Trigger}
        openState={openState}
        attribute={
          attribute
            ? {
                key: attribute.key,
                value:
                  attribute.values.find(value => value.isSelected)?.value ||
                  attribute.values[0].value,
              }
            : undefined
        }
        currencies={getBidCurrencies(marketplaceChain.id, collectionId)}
        oracleEnabled={marketplaceChain.oracleBidsEnabled}
        chainId={marketplaceChain.id}
        normalizeRoyalties={env.NEXT_PUBLIC_NORMALIZE_ROYALTIES}
        onClose={(_, __, currentStep) => {
          if (mutate && currentStep == BidStep.Complete) mutate();
        }}
        onBidComplete={() => {
          toast.success(t('toast.place-bid.success.title'), {
            description: t('toast.place-bid.success.description'),
          });
        }}
        onBidError={error => {
          if (error) {
            if ((error as any).cause.code && (error as any).cause.code === 4001) {
              toast.error(t('toast.general.transaction-cancelled.title'), {
                description: t('toast.general.transaction-cancelled.description'),
              });
              return;
            }
          }
          toast.error(t('toast.place-bid.transaction-incomplete.title'));
        }}
        onViewOffers={() =>
          router.push(
            routes.profile(userAddress ?? '', marketplaceChain.routePrefix, { tab: 'offers' }),
          )
        }
      />
    </AuthenticationTriggerWrapper>
  );
};

export default CollectionOffer;
